/**
 * Feedback Detail Modal Component
 * 
 * Full-screen modal for viewing feedback details with:
 * - Title with accent bar
 * - Full description
 * - Comments section
 * - Sidebar with metadata
 * - Fade in/out animation
 * - Auth modal triggers for guests
 */
import { useState, useEffect } from '@wordpress/element';
import { __ } from '@wordpress/i18n';
import CommentSection from './CommentSection';

const FeedbackDetailModal = ({ 
    feedback, 
    statuses = {},
    onClose, 
    onUpvote,
    onDownvote, 
    isLoggedIn, 
    currentUser,
    restUrl,
    nonce,
    allowComments,
    enableDownvoting,
    isPremium,
    onAuthRequired,
    onCommentCountChange
}) => {
    const [loading, setLoading] = useState(false);
    const [fullData, setFullData] = useState(feedback);
    const [isClosing, setIsClosing] = useState(false);

    // Sync fullData with feedback prop when it changes (e.g., after upvote)
    useEffect(() => {
        setFullData(feedback);
    }, [feedback]);

    // Build dynamic status label/color from prop.
    const getStatusLabel = (slug) => {
        if (statuses[slug] && statuses[slug].label) return statuses[slug].label;
        return slug;
    };

    const getStatusColor = (slug) => {
        const fallback = { bg: '#f3f4f640', text: '#6b7280' };
        if (statuses[slug] && statuses[slug].color) {
            return { bg: statuses[slug].color + '20', text: statuses[slug].color };
        }
        return fallback;
    };

    const categoryLabels = {
        'feature-requests': __( 'Feature Request', 'changeloger' ),
        'bug-reports': __( 'Bug Report', 'changeloger' ),
        'integrations': __( 'Integration', 'changeloger' ),
        'languages': __( 'Language', 'changeloger' )
    };

    // Format relative date
    const getRelativeDate = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diff = Math.floor((now - date) / (1000 * 60 * 60 * 24));
        
        if (diff === 0) return __( 'Today', 'changeloger' );
        if (diff === 1) return __( 'Yesterday', 'changeloger' );
        if (diff < 7) return `${diff} days ago`;
        if (diff < 30) return `${Math.floor(diff / 7)} weeks ago`;
        return date.toLocaleDateString();
    };

    // Handle close with animation
    const handleClose = () => {
        setIsClosing(true);
        setTimeout(() => {
            onClose();
        }, 200);
    };

    // Handle backdrop click
    const handleBackdropClick = (e) => {
        if (e.target.classList.contains('cha-modal-overlay')) {
            handleClose();
        }
    };

    // Handle upvote - trigger auth for guests
    const handleUpvote = () => {
        if (onUpvote) onUpvote(fullData.id);
    };

    // Handle downvote - trigger auth for guests
    const handleDownvote = () => {
        if (onDownvote) onDownvote(fullData.id);
    };

    // Handle escape key
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape') handleClose();
        };
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, []);

    // Prevent body scroll when modal is open
    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = '';
        };
    }, []);

    if (!fullData) return null;

    const status = fullData.status || 'in_review';
    const category = fullData.category?.slug || fullData.category || 'feature_requests';
    const currentStatusColor = getStatusColor(status);

    // Calculate net votes
    const netVotes = (fullData.upvotes || 0) - (fullData.downvotes || 0);

    return (
        <div 
            className={`cha-modal-overlay active ${isClosing ? 'closing' : ''}`} 
            onClick={handleBackdropClick}
        >
            <div className={`cha-feedback-detail-modal ${isClosing ? 'closing' : ''}`}>
                {/* Close button */}
                <button className="cha-detail-close" onClick={handleClose}>
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>

                <div className="cha-detail-layout">
                    {/* Main Content */}
                    <div className="cha-detail-main">
                        {/* Title with accent bar */}
                        <div className="cha-detail-header">
                            <div className="cha-detail-accent-bar"></div>
                            <h2 className="cha-detail-title">{fullData.title}</h2>
                        </div>

                        {/* Description */}
                        <div 
                            className="cha-detail-description"
                            dangerouslySetInnerHTML={{ __html: fullData.content || '<p>' + __( 'No description provided.', 'changeloger' ) + '</p>' }}
                        />

                        {/* Comments Section - only show if enabled */}
                        {allowComments && isPremium && (
                            <div className="cha-detail-comments">
                                <CommentSection
                                    feedbackId={fullData.id}
                                    isLoggedIn={isLoggedIn}
                                    currentUser={currentUser}
                                    onAuthRequired={onAuthRequired}
                                    onCommentAdded={(commentCount) => {
                                        setFullData(prev => {
                                            const newCount = commentCount != null
                                                ? commentCount
                                                : (prev.comment_count || 0) + 1;
                                            // Notify parent to update the card's comment count.
                                            if (onCommentCountChange) {
                                                onCommentCountChange(prev.id, newCount);
                                            }
                                            return {
                                                ...prev,
                                                comment_count: newCount
                                            };
                                        });
                                    }}
                                />
                            </div>
                        )}
                    </div>

                    {/* Sidebar */}
                    <div className="cha-detail-sidebar">
                        {/* Voting - Always clickable, triggers auth for guests */}
                        <div className="cha-sidebar-section">
                            <label>{__( 'Votes', 'changeloger' )}</label>
                            <div className="cha-upvoters-display">
                                <button 
                                    className={`cha-upvote-btn ${fullData.has_upvoted || fullData.has_voted ? 'voted' : ''}`}
                                    onClick={handleUpvote}
                                    title={__( 'Upvote', 'changeloger' )}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                    </svg>
                                </button>
                                <span className="cha-upvote-count">{enableDownvoting ? netVotes : (fullData.upvotes || 0)}</span>
                                {enableDownvoting && isPremium && (
                                    <button 
                                        className={`cha-downvote-btn ${fullData.has_downvoted ? 'voted' : ''}`}
                                        onClick={handleDownvote}
                                        title={__( 'Downvote', 'changeloger' )}
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                        </svg>
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Status */}
                        <div className="cha-sidebar-section">
                            <label>{__( 'Status', 'changeloger' )}</label>
                            <span 
                                className="cha-status-badge-detail"
                                style={{ 
                                    backgroundColor: currentStatusColor.bg, 
                                    color: currentStatusColor.text 
                                }}
                            >
                                {getStatusLabel(status)}
                            </span>
                        </div>

                        {/* Board/Category */}
                        <div className="cha-sidebar-section">
                            <label>{__( 'Board', 'changeloger' )}</label>
                            <span className="cha-board-badge">
                                <span className="cha-board-icon">💡</span>
                                {categoryLabels[category] || category}
                            </span>
                        </div>

                        {/* Date */}
                        <div className="cha-sidebar-section">
                            <label>{__( 'Date', 'changeloger' )}</label>
                            <span className="cha-date-text">{getRelativeDate(fullData.date)}</span>
                        </div>

                        {/* Author */}
                        <div className="cha-sidebar-section">
                            <label>{__( 'Author', 'changeloger' )}</label>
                            <div className="cha-author-display">
                                {fullData.author?.avatar && (
                                    <img 
                                        src={fullData.author.avatar} 
                                        alt="" 
                                        className="cha-author-avatar"
                                    />
                                )}
                                <span className="cha-author-name">
                                    {fullData.author?.name || __( 'Anonymous', 'changeloger' )}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FeedbackDetailModal;
